import { useState, useRef, useEffect } from 'react'

import { cn } from '@/lib/utils'
import { useDeferredActivation } from '@/hooks/useDeferredActivation'

type ProgressProps = {
  total: number
  align?: 'left' | 'center' | 'right' | 'custom'
  color?: 'primary' | 'secondary'
  activeIndex: number
  setActiveIndex: (index: number) => void
  position?: string
  size?: number
  autoStart?: 'immediate' | 'afterInteraction'
}

export default function Progress({
  total,
  align = 'right',
  color = 'primary',
  activeIndex,
  setActiveIndex,
  position,
  size = 24,
  autoStart = 'immediate',
}: ProgressProps) {
  const [remainingTime, setRemainingTime] = useState(10000)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const { ref, active } = useDeferredActivation<HTMLDivElement>({
    strategy: autoStart === 'afterInteraction' ? 'after-interaction-idle' : 'never',
  })
  const canAdvance = autoStart === 'afterInteraction' ? active : true

  useEffect(() => {
    setRemainingTime(10000)
    if (!canAdvance || total <= 1) return

    const start = Date.now()

    const updateRemaining = () => {
      const elapsed = Date.now() - start
      const newTime = Math.max(10000 - elapsed, 0)
      setRemainingTime(newTime)

      if (newTime === 0) {
        setActiveIndex((activeIndex + 1) % total)
      }
    }

    intervalRef.current = setInterval(updateRemaining, 100)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [canAdvance, setActiveIndex, activeIndex, total])

  const progress = ((10000 - remainingTime) / 10000) * 100
  const radius = 12
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset = ((100 - progress) / 100) * circumference
  const strokeWidth = size <= 24 ? 2.5 : 1

  return (
    <div
      ref={ref}
      className={cn(
        'absolute bottom-8',
        align === 'left' && 'left-8',
        align === 'center' && 'left-1/2 -translate-x-1/2',
        align === 'right' && 'right-8',
        align === 'custom' && position && position,
      )}
      style={{ width: size, height: size }}
    >
      <svg className="h-full w-full -rotate-90" viewBox="0 0 32 32">
        {/* Background circle */}
        <circle
          cx="16"
          cy="16"
          r={radius}
          strokeWidth={strokeWidth}
          fill="none"
          className="stroke-white/20"
        />
        {/* Progress circle */}
        <circle
          cx="16"
          cy="16"
          r={radius}
          strokeWidth={strokeWidth}
          fill="none"
          className={cn('stroke-primary-500', color === 'secondary' && 'stroke-white')}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={{ transition: 'stroke-dashoffset 100ms linear' }}
        />
      </svg>
    </div>
  )
}
